import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"
import styled from "styled-components"

import { IDLE, PENDING, STORAGE_MICROSOFT } from "const"
import {
    MtDriveCloud,
    MicrosoftCredentials,
} from "server/models"
import { StateType, useInput } from "client/utils"
import {
    StoreType,
    MicrosoftCreateServerClient,
    MicrosoftGenerateAuthUrl,
    MicrosoftConfirmCode,
    CreateCloudDriveSettings,
    SaveAccountSettings,

    updateAccountSettings,
    setSystemAccountSettings,
    closeModal,
} from "client/store"
import {
    Container,
    PageContent,
    PageHeader,
    PageTitle,
    Collapse,
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

const ConfirmLink = styled.a`
    text-decoration: underline;
`

let isMounted: boolean

const MicrosoftDriveSecrets = () => {
    const accountSettings = useSelector((state: StoreType) => state.accountSettings.settings)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [credentialsClientId, setCredentialsClientId] = useInput("")
    const [credentialsClientSecret, setCredentialsClientSecret] = useInput("")
    const [credentialsJSON, setCredentialsJSON] = useState<string>(null)
    const [authUrl, setAuthUrl] = useState<string>(null)
    const [confirmCode, setConfirmCode] = useInput("")

    const [credentialsValidation, setCredentialsValidation] = useState<string>(null)
    const [confirmCodeValidation, setConfirmCodeValidation] = useState<string>(null)

    const [credentialsLoadState, setCredentialsLoadState] = useState<StateType>(IDLE)
    const [confirmCodeState, setConfirmCodeState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const credentials: MicrosoftCredentials = {
            clientId: credentialsClientId,
            clientSecret: credentialsClientSecret,
        }
        setCredentialsJSON(JSON.stringify(credentials))
    }, [credentialsClientId, credentialsClientSecret])

    const onClose = () => {
        if (!isMounted) return
        if (modals.length) dispatch(closeModal())
    }

    const onSetCredentials = () => {
        setCredentialsLoadState(PENDING)

        MicrosoftCreateServerClient({
            accountId: userData.accountId,
            credentialsJSON,
        }).then(() => {
            MicrosoftGenerateAuthUrl(
                { accountId: userData.accountId },
                lang,
            ).then((res) => {
                setCredentialsLoadState(IDLE)
                if (res.validation) setCredentialsValidation(res.validation)
                else {
                    setAuthUrl(res.authUrl)
                    window.open(res.authUrl, "_blank").focus()
                }
            })
        })
    }

    const onConfirmCode = () => {
        setConfirmCodeState(PENDING)
        MicrosoftConfirmCode(
            {
                accountId: userData.accountId,
                confirmCode,
            },
            lang,
        ).then((res) => {
            if (res.validation) {
                setConfirmCodeState(IDLE)
                setConfirmCodeValidation(res.validation)
                return
            }

            const cloudDrive: MtDriveCloud = {
                accountId: userData.accountId,
                storage: STORAGE_MICROSOFT,
                credentials: credentialsJSON,
                token: JSON.stringify(res.token),
            }

            CreateCloudDriveSettings(cloudDrive).then(() => {
                dispatch(updateAccountSettings( // update in store
                    "drive",
                    {
                        defaultStorage: STORAGE_MICROSOFT,
                        microsoftDriveConnected: true,
                    },
                ))

                const accountSettingsCopy = { ...accountSettings }
                accountSettingsCopy.drive = { // update for saving
                    ...accountSettingsCopy.drive,
                    defaultStorage: STORAGE_MICROSOFT,
                    microsoftDriveConnected: true,
                }

                SaveAccountSettings(userData.accountId, accountSettingsCopy)
                .then(() => {
                    dispatch(setSystemAccountSettings(accountSettingsCopy))
                    setConfirmCodeState(IDLE)
                    onClose()
                })
            })
        })
    }

    const confirmLink = lang.cloudDriveConfirmLink.split("{0}")

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.microsoftOneDriveSecrets}</PageTitle>
            </PageHeader>
            <PageContent>
                <Collapse
                    show
                    title={lang.connectDriveStep1SetCredentials}
                >
                    <PageForm>
                        <Row>
                            <Col xs={12}>
                                <FormGroup>
                                    <Label>{lang.credentialsApplicationClientId}</Label>
                                    <Input type="text" value={credentialsClientId} onChange={setCredentialsClientId} />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{lang.credentialsClientSecret}</Label>
                                    <Input type="text" value={credentialsClientSecret} onChange={setCredentialsClientSecret} />
                                    <Feedback>{credentialsValidation && credentialsValidation}</Feedback>
                                </FormGroup>
                                <Button
                                    variant="success"
                                    disabled={!credentialsClientId.length || !credentialsClientSecret.length}
                                    onClick={onSetCredentials}
                                >
                                    {credentialsLoadState === PENDING ? <SpinnerIcon /> : lang.connect}
                                </Button>
                            </Col>
                        </Row>
                    </PageForm>
                </Collapse>
                {
                    authUrl !== null && (
                        <Collapse
                            show
                            title={lang.connectDriveStep2EnterConfirmCode}
                        >
                            <PageForm>
                                <Row>
                                    <Col xs={12}>
                                        <p>
                                            {confirmLink[0]}
                                            <ConfirmLink href={authUrl} target="_blank" rel="noopener noreferrer">
                                                {lang.thisLink}
                                            </ConfirmLink>
                                            {confirmLink[1]}
                                        </p>

                                        <FormGroup>
                                            <Label>{lang.confirmCode}</Label>
                                            <Input type="text" value={confirmCode} onChange={setConfirmCode} />
                                            <Feedback>{confirmCodeValidation && confirmCodeValidation}</Feedback>
                                        </FormGroup>
                                        <Button
                                            variant="success"
                                            disabled={!confirmCode.length}
                                            onClick={onConfirmCode}
                                        >
                                            {confirmCodeState === PENDING ? <SpinnerIcon /> : lang.confirm}
                                        </Button>
                                    </Col>
                                </Row>
                            </PageForm>
                        </Collapse>
                    )
                }
            </PageContent>
        </Container>
    )
}
export default MicrosoftDriveSecrets
